import { getDataTable } from '../../features/visual-analytics/charts/chartDataBindings';
import {
  convertCatNameToCatIndex,
  convertColumnDataToDisplayString,
} from '../../features/visual-analytics/helpers/misc';
import { getColumnInfo } from '../analyticsDataSlice';
import { TpCanonicalTypeInfo, TpDataContext, VALTYPES } from '../interfaces';
import {
  TpActiveEnSelectionFilter,
  TpEnSelectionFilterDefinition,
  TpEnSelectionFilterStep,
  TpEnSelectionFilterStepCategorical,
  TpEnSelectionFilterTypes,
} from './interfaces';
import { getEnSelectionFilterStep, removeEnSelectionFilterStep, smartRenderEnFilterItems } from './selectionEnFilters';
import { TpUserEnSelectionAction, TpUserEnSelectionActionCategorical } from './userSelectionActions';

function getCatEnFilterStepKey(dataTableId: string, colId: string): string {
  return `categoricalFilterStep_${dataTableId}_${colId}`;
}

export function getCategoricalEnFilterStep(
  selectionFilter: TpActiveEnSelectionFilter,
  dataTableId: string,
  colId: string,
): TpEnSelectionFilterStepCategorical | null {
  const step = getEnSelectionFilterStep(selectionFilter, getCatEnFilterStepKey(dataTableId, colId));
  if (!step) return null;
  return step as TpEnSelectionFilterStepCategorical;
}

function handleUserAction(
  state: any,
  dataContext: TpDataContext,
  canonicalTypeInfo: TpCanonicalTypeInfo,
  userSelectionAction: TpUserEnSelectionAction,
) {
  const userSelectionActionCategorical = userSelectionAction as TpUserEnSelectionActionCategorical;
  const exclude = userSelectionActionCategorical.exclude;
  const key = getCatEnFilterStepKey(userSelectionActionCategorical.dataTableId, userSelectionActionCategorical.colId);
  const step = getEnSelectionFilterStep(canonicalTypeInfo.selectionFilter, key);
  let stepCat: TpEnSelectionFilterStepCategorical;
  if (!step) {
    // step not yet present - we create it
    stepCat = {
      type: TpEnSelectionFilterTypes.CATEGORICAL,
      key,
      dataTableId: userSelectionActionCategorical.dataTableId,
      colId: userSelectionActionCategorical.colId,
      selectedCategoryIdList: !exclude ? [userSelectionActionCategorical.selectedCategoryId] : [],
      excludedCategoryIdList: exclude ? [userSelectionActionCategorical.selectedCategoryId] : [],
    };
    canonicalTypeInfo.selectionFilter.steps.push(stepCat);
  } else {
    // step already present - we modify it
    stepCat = step as TpEnSelectionFilterStepCategorical;
    if (
      stepCat.selectedCategoryIdList.includes(userSelectionActionCategorical.selectedCategoryId) ||
      stepCat.excludedCategoryIdList.includes(userSelectionActionCategorical.selectedCategoryId)
    ) {
      // item is present -  we remove it
      stepCat.selectedCategoryIdList = stepCat.selectedCategoryIdList.filter(
        (item) => item != userSelectionActionCategorical.selectedCategoryId,
      );
      stepCat.excludedCategoryIdList = stepCat.excludedCategoryIdList.filter(
        (item) => item != userSelectionActionCategorical.selectedCategoryId,
      );
      if (stepCat.selectedCategoryIdList.length == 0 && stepCat.excludedCategoryIdList.length == 0) {
        // since no more items are left, we remove the entire filter step
        removeEnSelectionFilterStep(canonicalTypeInfo.selectionFilter, key);
      }
    } else {
      // item not yet presebt -  we add it
      if (!exclude) stepCat.selectedCategoryIdList.push(userSelectionActionCategorical.selectedCategoryId);
      else stepCat.excludedCategoryIdList.push(userSelectionActionCategorical.selectedCategoryId);
    }
  }
}

function performStep(
  dataContext: TpDataContext,
  canonicalTypeInfo: TpCanonicalTypeInfo,
  step: TpEnSelectionFilterStep,
): any[] {
  const stepCat = step as TpEnSelectionFilterStepCategorical;
  const dataTable = getDataTable(dataContext, stepCat.dataTableId);
  const colInfo = getColumnInfo(dataTable, stepCat.colId);
  const itemIdList = dataTable.columnData[dataTable.colItemId];
  const catValList = dataTable.columnData[stepCat.colId];
  const selectedItemIds = [];

  if (stepCat.selectedCategoryIdList.length == 1 && stepCat.excludedCategoryIdList.length == 0) {
    // faster version in case a single category is selected
    const selCat = stepCat.selectedCategoryIdList[0];
    if (colInfo.valueType != VALTYPES.MULTICAT) {
      for (let itemIdx = 0; itemIdx < itemIdList.length; itemIdx++)
        if (catValList[itemIdx] == selCat) selectedItemIds.push(itemIdList[itemIdx]);
    } else {
      for (let itemIdx = 0; itemIdx < itemIdList.length; itemIdx++)
        if (catValList[itemIdx].includes(selCat)) selectedItemIds.push(itemIdList[itemIdx]);
    }
  } else {
    if (colInfo.valueType != VALTYPES.MULTICAT) {
      for (let itemIdx = 0; itemIdx < itemIdList.length; itemIdx++)
        if (
          (stepCat.selectedCategoryIdList.length == 0 ||
            stepCat.selectedCategoryIdList.includes(catValList[itemIdx])) &&
          !stepCat.excludedCategoryIdList.includes(catValList[itemIdx])
        )
          selectedItemIds.push(itemIdList[itemIdx]);
    } else {
      for (let itemIdx = 0; itemIdx < itemIdList.length; itemIdx++) {
        let isMatchInIncluded = false;
        if (stepCat.selectedCategoryIdList.length == 0) isMatchInIncluded = true;
        for (const selCatVal of stepCat.selectedCategoryIdList)
          if (catValList[itemIdx].includes(selCatVal)) isMatchInIncluded = true;

        let isMatchInExcluded = false;
        for (const selCatVal of stepCat.excludedCategoryIdList)
          if (catValList[itemIdx].includes(selCatVal)) isMatchInExcluded = true;

        if (isMatchInIncluded && !isMatchInExcluded) selectedItemIds.push(itemIdList[itemIdx]);
      }
    }
  }
  return selectedItemIds;
}

function renderContent(
  dataContext: TpDataContext,
  canonicalTypeInfo: TpCanonicalTypeInfo,
  step: TpEnSelectionFilterStep,
  forceString?: boolean,
) {
  const stepCat = step as TpEnSelectionFilterStepCategorical;
  const dataTable = getDataTable(dataContext, stepCat.dataTableId);
  const colInfo = getColumnInfo(dataTable, stepCat.colId);
  const missingValue = '(unknown value)';
  const valuesList = stepCat.selectedCategoryIdList.map(
    (catId) => convertColumnDataToDisplayString(colInfo, catId) ?? missingValue,
  );
  const excludedList = stepCat.excludedCategoryIdList.map(
    (catId) => convertColumnDataToDisplayString(colInfo, catId) ?? missingValue,
  );

  if (forceString) {
    if (excludedList.length == 0) {
      return `${colInfo.name}=${smartRenderEnFilterItems(valuesList, true)}`;
    }
    if (valuesList.length == 0) {
      return `${colInfo.name}= NOT(${smartRenderEnFilterItems(excludedList, true)})`;
    }
    return `${colInfo.name}=${smartRenderEnFilterItems(valuesList, true)} AND NOT(${smartRenderEnFilterItems(
      excludedList,
      true,
    )})`;
  }
  if (excludedList.length == 0) {
    return (
      <span>
        <b>{colInfo.name}</b> = {smartRenderEnFilterItems(valuesList)}
      </span>
    );
  }
  if (valuesList.length == 0) {
    return (
      <span>
        <b>{colInfo.name}</b> = NOT {smartRenderEnFilterItems(excludedList)}
      </span>
    );
  }
  return (
    <span>
      <b>{colInfo.name}</b> = {smartRenderEnFilterItems(valuesList)} AND NOT {smartRenderEnFilterItems(excludedList)}
    </span>
  );
}

function getJsonSchema() {
  return {
    type: 'object',
    title: TpEnSelectionFilterTypes.CATEGORICAL,
    properties: {
      type: { type: 'string', const: TpEnSelectionFilterTypes.CATEGORICAL },
      colId: { type: 'string' },
      categories: { type: 'array', items: { type: 'string' } },
    },
    required: ['type', 'colId', 'categories'],
  };
}

function fromJson(dataContext: TpDataContext, dataTableId: string, jsonData: any): TpEnSelectionFilterStep {
  const colId = jsonData.colId;
  const dataTable = getDataTable(dataContext, dataTableId);
  const columnInfo = getColumnInfo(dataTable, colId);
  const filter: TpEnSelectionFilterStepCategorical = {
    type: TpEnSelectionFilterTypes.CATEGORICAL,
    key: getCatEnFilterStepKey(dataTableId, colId),
    dataTableId,
    colId,
    selectedCategoryIdList: jsonData.categories.map((catName: string) => convertCatNameToCatIndex(columnInfo, catName)),
    excludedCategoryIdList: [],
  };
  return filter;
}

function toJson(dataContext: TpDataContext, dataTableId: string, step: TpEnSelectionFilterStep): any {
  const stepCat = step as TpEnSelectionFilterStepCategorical;
  const dataTable = getDataTable(dataContext, dataTableId);
  const columnInfo = getColumnInfo(dataTable, stepCat.colId);
  return {
    type: TpEnSelectionFilterTypes.CATEGORICAL,
    colId: stepCat.colId,
    categories: stepCat.selectedCategoryIdList
      .map((catId) => convertColumnDataToDisplayString(columnInfo, catId) ?? undefined)
      .filter((catName) => catName !== undefined),
  };
}

export const defEnSelectionFilterCategorical: TpEnSelectionFilterDefinition = {
  handleUserAction,
  performStep,
  renderContent,
  getJsonSchema,
  fromJson,
  toJson,
};
